import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { zhCN } from 'date-fns/locale';

// Number formatting
export function formatNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatPrice(value: number): string {
  return formatNumber(value, 2);
}

export function formatPercent(value: number, showSign = true, isDecimal = true): string {
  // If value is a decimal (0.05 = 5%), multiply by 100
  const percentValue = isDecimal && Math.abs(value) < 1 ? value * 100 : value;
  const formatted = formatNumber(percentValue, 2);
  if (showSign && percentValue > 0) {
    return `+${formatted}%`;
  }
  return `${formatted}%`;
}

export function formatVolume(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 100000000) {
    return `${sign}${formatNumber(abs / 100000000, 2)}亿`;
  }
  if (abs >= 10000) {
    return `${sign}${formatNumber(abs / 10000, 2)}万`;
  }
  return `${sign}${formatNumber(abs, 0)}`;
}

// Inputs are in CNY 元 (raw yuan). Negative values represent net outflows;
// must scale by absolute value before unit suffix or "−24,807,400.00" (raw 元)
// leaks past the 亿/万 threshold and we get a mixed-unit table.
export function formatAmount(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 100000000) {
    return `${sign}${formatNumber(abs / 100000000, 2)}亿`;
  }
  if (abs >= 10000) {
    return `${sign}${formatNumber(abs / 10000, 2)}万`;
  }
  return `${sign}${formatNumber(abs, 2)}`;
}

// Signed-flow variant: always shows + or − prefix so callers don't have to
// branch on sign for net inflow/outflow display. Pass an absolute zero
// (== 0 exact) through unchanged so the table reads "0.00" not "+0.00".
export function formatFlow(value: number): string {
  if (value === 0) return formatAmount(0);
  const formatted = formatAmount(Math.abs(value));
  return value > 0 ? `+${formatted}` : `-${formatted}`;
}

export function formatMarketCap(value: number): string {
  if (value >= 1000000000000) {
    return `${formatNumber(value / 1000000000000, 2)}万亿`;
  }
  if (value >= 100000000) {
    return `${formatNumber(value / 100000000, 2)}亿`;
  }
  if (value >= 10000) {
    return `${formatNumber(value / 10000, 2)}万`;
  }
  return formatNumber(value, 0);
}

// Date formatting
export function formatDate(date: string | Date, pattern = 'yyyy-MM-dd'): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, pattern, { locale: zhCN });
}

export function formatDateTime(date: string | Date): string {
  return formatDate(date, 'yyyy-MM-dd HH:mm:ss');
}

export function formatTime(date: string | Date): string {
  return formatDate(date, 'HH:mm:ss');
}

export function formatRelative(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(d, { addSuffix: true, locale: zhCN });
}

// Trading specific
export function formatPriceChange(
  current: number,
  previous: number
): { value: number; percent: number; direction: 'up' | 'down' | 'neutral' } {
  const value = current - previous;
  const percent = previous !== 0 ? (value / previous) * 100 : 0;
  const direction = value > 0 ? 'up' : value < 0 ? 'down' : 'neutral';

  return { value, percent, direction };
}
